import { useEffect, useState } from "react";
import { useQuestionnaireStore } from "../../store/useQuestionnaireStore";
import { useClientStore } from "../../store/useClientStore";
import { useProjectTypeStore } from "../../store/useProjectTypeStore";
import { Loader2, Plus, Eye } from "lucide-react";
import InputField from "../ui/InputField";
import SelectField from "../ui/SelectField";
import Button from "../ui/Button";
import QuestionInput from "./QuestionInput";
import QuestionnairePreview from "./QuestionnairePreview";

const EditQuestionnaireModal = ({ questionnaire, onClose }: any) => {
  const { updateQuestionnaire, isUpdating } = useQuestionnaireStore();
  const { clients, fetchClients } = useClientStore();
  const { types, fetchTypes } = useProjectTypeStore();

  // Opened straight from a deep link the pickers would otherwise be empty, so
  // load their options here rather than relying on the list page having run.
  useEffect(() => {
    fetchClients();
    fetchTypes();
  }, [fetchClients, fetchTypes]);

  const [showPreview, setShowPreview] = useState(false);
  const [name, setName] = useState(questionnaire.name);
  const [description, setDescription] = useState(questionnaire.description);
  const [serviceType, setServiceType] = useState(questionnaire.serviceType);
  const [layoutType, setLayoutType] = useState<"form" | "card">(questionnaire.layoutType || "form");
  // Preserve any existing project link on save (project is linked from the
  // Project form, not edited here).
  const [projectId] = useState(questionnaire.projectId?._id || questionnaire.projectId);
  const [clientId, setClientId] = useState(questionnaire.clientId?._id || questionnaire.clientId);
  const [questions, setQuestions] = useState(questionnaire.questions);

  // Admin-managed project types, plus whatever this questionnaire was saved
  // with — so an archived type still shows instead of silently resetting.
  const projectTypeOptions = [
    { value: "", label: "Select Service Type" },
    ...types.map((t) => ({ value: t.name, label: t.name })),
    ...(serviceType && !types.some((t) => t.name === serviceType)
      ? [{ value: serviceType, label: `${serviceType} (archived)` }]
      : []),
  ];

  const addQuestion = () =>
    setQuestions([
      ...questions,
      { question: "", answerType: "text", options: [""], answer: "", required: false, placeholder: "" },
    ]);

  const updateQuestion = (index: number, field: string, value: any) => {
    const updated = [...questions];
    updated[index][field] = value;
    setQuestions(updated);
  };

  const toggleRequired = (index: number) => {
    const updated = [...questions];
    updated[index].required = !updated[index].required;
    setQuestions(updated);
  };

  const addQuestionOption = (index: number) => {
    const updated = [...questions];
    updated[index].options.push("");
    setQuestions(updated);
  };

  const updateQuestionOption = (qIndex: number, optIndex: number, value: string) => {
    const updated = [...questions];
    updated[qIndex].options[optIndex] = value;
    setQuestions(updated);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    await updateQuestionnaire(questionnaire._id, {
      name,
      description,
      serviceType,
      layoutType,
      projectId,
      clientId,
      questions,
    });
    onClose();
  };

  const previewData = {
    name,
    description,
    questions: questions.map((q: any) => ({
      question: q.question,
      answerType: q.answerType,
      options: q.options || [],
      required: q.required,
      placeholder: q.placeholder || "",
    })),
  };

  return (
    <>
      <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-2xl p-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-6">Edit Questionnaire</h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <InputField
            label="Questionnaire Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter questionnaire title"
            required
          />

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Briefly describe what this questionnaire is for..."
              className="w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#2462b2] outline-none"
              rows={3}
            />
          </div>

          <SelectField
            label="Service Type"
            value={serviceType}
            onChange={(e) => setServiceType(e.target.value)}
            options={projectTypeOptions}
            required
          />

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Layout Type</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { value: "form", title: "Form Layout", desc: "Classic single page. (Default)" },
                { value: "card", title: "Card Layout", desc: "Step-by-step wizard — one card per question." },
              ].map((opt) => (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => setLayoutType(opt.value as "form" | "card")}
                  className={`text-left rounded-xl border-2 p-4 transition-all ${
                    layoutType === opt.value
                      ? "border-[#2462b2] bg-blue-50/60 ring-2 ring-[#2462b2]/20"
                      : "border-gray-200 hover:border-[#2462b2]/40"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-gray-800">{opt.title}</span>
                    <span
                      className={`w-4 h-4 rounded-full border-2 ${
                        layoutType === opt.value ? "border-[#2462b2] bg-[#2462b2]" : "border-gray-300"
                      }`}
                    />
                  </div>
                  <p className="text-[12px] text-gray-500 mt-1">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <SelectField
            label="Contact"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            options={[
              { value: "", label: "Select a contact…" },
              ...clients.map((c: any) => ({ value: c._id, label: c.name })),
            ]}
            required
          />

          <div className="space-y-5">
            <label className="block text-sm font-bold text-gray-700">Questions</label>

            {questions.map((q: any, i: number) => (
              <div key={i} className="p-4 bg-gray-50 border rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-700">Question {i + 1}</h3>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={q.required}
                      onChange={() => toggleRequired(i)}
                      className="w-4 h-4"
                    />
                    Required
                  </label>
                </div>

                <InputField
                  label="Question Text"
                  value={q.question}
                  onChange={(e) => updateQuestion(i, "question", e.target.value)}
                  placeholder="Enter question text"
                  required
                />

                <InputField
                  label="Placeholder (Optional)"
                  value={q.placeholder || ""}
                  onChange={(e) => updateQuestion(i, "placeholder", e.target.value)}
                  placeholder="Enter input placeholder text"
                />

                <SelectField
                  label="Answer Type"
                  value={q.answerType}
                  onChange={(e) => updateQuestion(i, "answerType", e.target.value)}
                  options={[
                    { value: "text", label: "Text" },
                    { value: "date", label: "Date" },
                    { value: "radio", label: "Radio Buttons" },
                    { value: "checkbox", label: "Checkboxes" },
                    { value: "select", label: "Dropdown Select" },
                  ]}
                />

                {["radio", "checkbox", "select"].includes(q.answerType) && (
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">Options</label>
                    {(q.options || []).map((opt: string, optIndex: number) => (
                      <InputField
                        key={optIndex}
                        label={`Option ${optIndex + 1}`}
                        value={opt}
                        onChange={(e) => updateQuestionOption(i, optIndex, e.target.value)}
                        placeholder="Enter option value"
                      />
                    ))}

                    <button
                      type="button"
                      onClick={() => addQuestionOption(i)}
                      className="text-[#2462b2] font-bold hover:underline"
                    >
                      + Add Option
                    </button>
                  </div>
                )}
              </div>
            ))}

            <button
              type="button"
              onClick={addQuestion}
              className="inline-flex items-center gap-2 text-[#2462b2] font-bold hover:underline"
            >
              <Plus size={18} /> Add Another Question
            </button>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={() => setShowPreview(true)}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-white border-2 border-[#2462b2] text-[#2462b2] font-bold rounded-xl px-6 py-3 hover:bg-[#2462b2] hover:text-white transition-all duration-200"
            >
              <Eye size={20} />
              Preview Changes
            </button>
            <Button type="submit" disabled={isUpdating} className="flex-1">
              {isUpdating ? (
                <span className="flex items-center gap-2 justify-center">
                  <Loader2 size={18} className="animate-spin" /> Updating...
                </span>
              ) : (
                "Update Questionnaire"
              )}
            </Button>
          </div>
        </form>
      </div>

      {showPreview && (
        <QuestionnairePreview formData={previewData} layout={layoutType} onClose={() => setShowPreview(false)} />
      )}
    </>
  );
};

export default EditQuestionnaireModal;
